import { useMemo } from 'react';
import styled from 'styled-components/macro';
import { Link } from 'react-router-dom';
import FuturaVisualization from 'components/FuturaVisualization';
import { Flex, Box, Tooltip, ClickableBox } from 'components/ui';
import remixPreviewEmpty from 'img/remix-preview-empty.svg';
import futuraSpec from 'utils/futuraSpec.json';
import { RemixData } from 'utils/futura';
import genIcon from 'img/gen.svg';
import tempoIcon from 'img/metronome.svg';
import shapePlaceholder from 'img/shape-placeholder.svg';
import square from 'img/square.svg';
import triangle from 'img/triangle.svg';
import line from 'img/line.svg';
import { FUTURA_PALETTES } from '../../constants';

const SLink = styled(Link)`
  color: white;
  font-weight: bold;
`;

enum NoteShape {
  Square='Square',
  Triangle='Triangle',
  Line='Line',
}

const noteShapeImages = {
  [NoteShape.Square]: square,
  [NoteShape.Triangle]: triangle,
  [NoteShape.Line]: line,
}

interface RemixPreviewProps {
  id: string;
  mixCode?: string;
  remixData?: RemixData;
  tempo: number;
  showDetails?: boolean;
}

const EBF = window.EulerBeatsFutura;

const RemixPreview = ({ id, mixCode, remixData, tempo, showDetails=true }: RemixPreviewProps) => {
  const pieceSpec = useMemo(() => (
    EBF.utils.updatePieceSpecRaw(futuraSpec)
  ), []);

  const pieceData = useMemo(() => (
    EBF.utils.buildPieceData({
      pieceSpec,
      mixCode,
      tempo,
    })),
    [mixCode, pieceSpec, tempo]
  );

  const palette = FUTURA_PALETTES.find(p => p.name === remixData?.paletteName) || {
    name: 'Placeholder',
    layers: ['#999', '#999', '#999', '#999', '#999'],
  };

  return (
    <Box my={3}>
      {
        (mixCode && remixData) ? (
          <>
            <FuturaVisualization
              elementId={id}
              width={320}
              height={320}
              pieceData={pieceData}
              twists={remixData.twists}
              distortion={remixData.distortion}
              paletteName={remixData.paletteName}
              noteShape={(remixData.noteShape as NoteShape)}
            />
          <Box my={2} fontSize="12px">
            Drag and drop to rotate. Press 'C' to change camera modes while playing.
          </Box>
          </>
        ) : (
          <Box width={['340px', '400px']} margin="0 auto" textAlign="center">
            <img src={remixPreviewEmpty} width="100%" alt="Remix Preview" />
          </Box>
        )
      }

      {
        showDetails && (
          <>
            <Flex my={3} backgroundColor="#121212" borderRadius="8px">
              <Flex width="33.3%" borderRight="1px solid #333" p={3}>
                <Box mr={3}>
                  <img src={genIcon} alt="Gen" width="32px" />
                </Box>
                <Box>
                  <Box fontSize="12px">Gen</Box>
                  <Box>{ remixData?.generation || '-' }</Box>
                </Box>
              </Flex>
              <Flex width="33.3%" borderRight="1px solid #333" p={3}>
                <Box mr={3}>
                  <img src={tempoIcon} alt="Tempo" width="32px" />
                </Box>
                <Box>
                  <Box fontSize="12px">Tempo</Box>
                  <Box>{ tempo || '-' }</Box>
                </Box>
              </Flex>
              <Tooltip id="tooltip_preview_palette" place="top" delayHide={500} clickable>
                <Box mb={2}>
                  Most of the selected clips have the {remixData?.paletteName} Color Palette associated to them.
                </Box>
                <Box>
                  <SLink to="/futura/traits#palettes" target="_blank">
                    Learn more about the Color Palette trait.
                  </SLink>
                </Box>
              </Tooltip>
              <Flex width="33.3%" p={3} data-for="tooltip_preview_palette" data-tip="tooltip_preview_palette_tip">
                <Box mr={3}>
                  { palette.layers.map((color, index) => (
                    <Box key={color + index} background={color} width="24px" height="4px" mb={1} />
                  )) }
                </Box>
                <Box>
                  <Box fontSize="12px">Palette</Box>
                  <Box>{ remixData?.paletteName || '-' }</Box>
                </Box>
              </Flex>
            </Flex>

            <Flex my={3} backgroundColor="#121212" borderRadius="8px">
              <Tooltip id="tooltip_preview_shape" place="top" delayHide={500} clickable>
                <Box mb={2}>
                  Most of the selected clips have the {remixData?.noteShape} Note Shape associated to them.
                </Box>
                <Box>
                  <SLink to="/futura/traits#note_shapes" target="_blank">
                    Learn more about the Note Shape trait.
                  </SLink>
                </Box>
              </Tooltip>
              <Flex width="33.3%" p={3} data-for="tooltip_preview_shape" data-tip="tooltip_preview_shape_tip">
                <Box mr={3}>
                  {
                    remixData
                      ? <img src={noteShapeImages[remixData.noteShape]} alt="Note Shape" width="32px" />
                      : <img src={shapePlaceholder} alt="Shape" width="32px" />
                  }
                </Box>
                <Box>
                  <Box fontSize="12px">Shape</Box>
                  <Box>{ remixData?.noteShape || '-' }</Box>
                </Box>
              </Flex>
              <Box minHeight="68px" width="66.6%" borderLeft="1px solid #333" p={3}>
                <Box fontSize="12px">Effects</Box>
                <Flex>
                  {remixData?.effects.map((effect, index) => (
                    <Box key={effect.name}>
                      <Tooltip id={`tooltip_preview_${effect.name}_${index}`} place="top" delayHide={500} clickable>
                        <Flex alignCenter>
                          <Box mr={2}><img src={effect.icon} alt={effect.name} width="24px" /></Box>
                          <Box>{effect.name}</Box>
                        </Flex>
                        <Box my={2}>
                          This effect was derived from clip: {effect.origin}.
                        </Box>
                        <Box>
                          <SLink to="/futura/traits#effects" target="_blank">
                            Learn more about Effect traits.
                          </SLink>
                        </Box>
                      </Tooltip>
                      <ClickableBox
                        mt={2}
                        key={effect.name}
                        mr={3}
                        data-for={`tooltip_preview_${effect.name}_${index}`}
                        data-tip={`tip_preview_${effect.name}_${index}`}
                      >
                        <img src={effect.icon} alt={effect.name} width="24px" />
                      </ClickableBox>
                    </Box>
                  ))}
                  {remixData?.effects.length === 0 && 'No Effects'}
                </Flex>
              </Box>
            </Flex>
          </>
        )
      }

    </Box>
  );
}

export default RemixPreview;
